// W8-2 — WHAT DATE A SHELF ITEM WEARS, AND WHAT IT SORTS ON.
//
// Owner's ruling: "a re-run is NEW work and files as newest ... the shelf orders by most-recent
// activity and displays the current date of the work." That is one rule, it applies to every room's
// shelf and to My Room, and before this it lived nowhere at all — which is how Chaela's map came to
// be regenerated on 30 July and displayed as 22 July, eight rows down a newest-first list.
//
// It is a pure law in `src/lib` for the reason every law here is: a rule about what a member SEES
// that lives inside an 16,000-line TSX file cannot be imported by a `.mjs` gate, so it can only ever
// be checked by a regex looking at its text. `scripts/w8-shelf-order.mjs` RUNS these.
//
// `import type` only — that is the entry requirement for this folder.

// The shape both shelf lists and My Room hand the client. `createdAt` is preserved as HISTORY
// exactly as ruled (when the work first existed); `updatedAt` is when it last changed; `activityAt`
// is the server's own answer for the more recent of the two, so the client never has to compute it
// and can never compute it differently from the ORDER BY that produced the list.
export type ShelfDated = {
  createdAt?: string | null;
  updatedAt?: string | null;
  activityAt?: string | null;
};

// One shelf row, as every shelf surface receives it. The type lives HERE rather than in App.tsx
// because the rule about which of its three dates a member actually sees lives here — a type in one
// file and the law governing it in another is how the two drift apart.
export type ShelfItem = ShelfDated & {
  id: string;
  kind: string;
  type: string;
  title: string;
  createdAt: string;
  inMyRoom: boolean;
  fromPreviousDetails?: boolean;
  // NIKA (owner, 2026-08-13): whether the engine ever wrote anything into this row. It rides HERE,
  // on the one shelf-row type every shelf surface receives, because the owner's report was that the
  // Shelves drawer and My Room read her differently — and a fact only one of them can see is how
  // that happens. Three states (R18): written, failed-under-a-saved-row, and the 1,959 rows that
  // carry no such key at all. `readingIsWritten` is the only reader.
  interpreted?: boolean | null;
};

const ms = (value?: string | null): number => {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// THE DATE THE SHELF SHOWS AND SORTS ON. Trust the server's `activityAt` when it is there, because
// it is the same expression the ORDER BY used. Fall back to the greater of the two dates for a row
// that predates this field, and to `createdAt` for anything older still — a shelf that renders a
// blank date because a legacy row lacked a column is a worse outcome than an approximate one.
export function shelfActivityAt(item: ShelfDated): string {
  if (item.activityAt) return item.activityAt;
  const created = item.createdAt || "";
  const updated = item.updatedAt || "";
  if (!updated) return created;
  if (!created) return updated;
  return ms(updated) > ms(created) ? updated : created;
}

export const shelfActivityMs = (item: ShelfDated): number => ms(shelfActivityAt(item));

// NEWEST FIRST, on activity. The one comparator every shelf sorts with.
export function byNewestActivity(a: ShelfDated, b: ShelfDated): number {
  return shelfActivityMs(b) - shelfActivityMs(a);
}

// WAS THIS RE-RUN SINCE IT WAS FIRST MADE? The shelf shows the current date of the work either way;
// this is what lets a surface say so out loud rather than silently swapping the date under her.
// A tolerance because an INSERT sets both stamps in the same statement and they can differ by a
// millisecond or two: without it every brand-new row would claim to have been updated.
const SAME_WRITE_TOLERANCE_MS = 2000;

export function wasRerun(item: ShelfDated): boolean {
  const created = ms(item.createdAt);
  const activity = shelfActivityMs(item);
  if (!created || !activity) return false;
  return activity - created > SAME_WRITE_TOLERANCE_MS;
}
